/** @jsx jsx */

import { Fragment } from 'react';

import { css, jsx } from '@emotion/core';
import {
  Colors,
  CopyTextInput,
  LegacyButton,
  LegacyIconButton,
  SkeletonLoader,
  SkeletonText,
  Spacer,
  Text,
} from '@zapier/design-system';

import Confirmation from 'app/developer-v3/components/Confirmation';
import { useDeployKeys } from 'app/developer-v3/hooks/useDeployKeys';

const Styles = {
  listItem: css`
    padding: 15px 0px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    :not(:last-of-type) {
      border-bottom: 1px solid ${Colors.GrayWarm4};
    }
  `,
  wrapperCopy: css`
    flex: 1 auto;
  `,
  helpBox: css`
    background-color: ${Colors.GrayWarm2};
    border-radius: 3px;
    padding: 30px;
  `,
  helpBoxContent: css`
    text-align: center;
  `,
  helpBoxImage: css`
    display: flex;
    align-items: flex-end;
  `,
};

const DeployKeys = () => {
  const {
    isLoadingKeys,
    isCreatingKey,
    isDeletingKey,
    keys,
    createKey,
    deleteKey,
  } = useDeployKeys();

  return (
    <section>
      <Text margin="0px 0px 25px 0px" tag="h1" type="SectionHeader">
        Deploy Keys
      </Text>
      {isLoadingKeys || isCreatingKey || isDeletingKey ? (
        <SkeletonLoader>
          <SkeletonText lines={2} size="heading" width="80%" />
        </SkeletonLoader>
      ) : (
        <Fragment>
          {keys.length ? (
            <ul>
              {keys.map(({ id, key }) => (
                <li css={Styles.listItem} key={key}>
                  <div css={Styles.wrapperCopy}>
                    <CopyTextInput aria-label="Deploy key" value={key} />
                  </div>
                  <Spacer width={10} />
                  <Confirmation onConfirm={() => deleteKey({ id })}>
                    {({ isConfirming, onClick }) => (
                      <LegacyIconButton
                        color={isConfirming ? 'icon-alt' : 'icon-primary'}
                        icon={isConfirming ? 'actionTrashFill' : 'formX'}
                        onClick={onClick}
                        size="medium"
                      />
                    )}
                  </Confirmation>
                </li>
              ))}
            </ul>
          ) : (
            <article css={Styles.helpBox}>
              <section css={Styles.helpBoxContent}>
                <Text tag="h3" type="Body4">
                  It looks like you haven't added a deploy key yet!
                </Text>
                <Spacer height={10} />
                <Text type="Body1">
                  Setup auth to Zapier's platform with a deploy key
                </Text>
                <Spacer height={10} />
                <LegacyButton
                  disabled={isLoadingKeys || isCreatingKey || isDeletingKey}
                  onClick={() => createKey()}
                >
                  Create Deploy Key
                </LegacyButton>
              </section>
            </article>
          )}
        </Fragment>
      )}
    </section>
  );
};

DeployKeys.defaultProps = {
  keys: [],
};

export default DeployKeys;
